// ── HVAC Templates ────────────────────────────────────────────────────────────
// T4: Climate Pro  — deep navy + ice blue, Outfit/Manrope, full-page lead-gen
// T5: Always On    — industrial neumorphic, Barlow Condensed/Barlow/IBM Plex Mono

export const HVAC_DEFAULT_COPY = {
  4: {
    eyebrow: "Northeast Ohio's HVAC Experts",
    headline: "Stay<br>Comfortable<br>All Year",
    tagline: "Heating, cooling, and air quality solutions for your home or business. Same-day service available.",
    about: "COMPANY_NAME has been keeping Northeast Ohio homes comfortable through every season. Our licensed technicians service all makes and models, with upfront pricing and no hidden fees.",
  },
  5: {
    eyebrow: "24/7 Emergency HVAC Service",
    headline: "Heat or AC<br><em>Down? We're<br>On Our Way.</em>",
    tagline: "Northeast Ohio's most reliable emergency HVAC team. Day or night, we show up fast.",
    about: "COMPANY_NAME provides fast, reliable heating and cooling repair across Northeast Ohio. When your system fails, we answer the call — licensed technicians on call around the clock.",
  },
};

export const HVAC_DEFAULT_SERVICES = {
  4: [
    { icon: "fa-temperature-high", title: "Heating Repair & Install", desc: "Furnace tune-ups, repairs, and full system replacements from all major manufacturers." },
    { icon: "fa-snowflake", title: "Air Conditioning", desc: "AC installation, emergency repairs, and seasonal maintenance to keep you cool all summer." },
    { icon: "fa-wind", title: "Air Quality", desc: "Whole-home air purifiers, humidifiers, and ventilation solutions for healthier indoor air." },
    { icon: "fa-bolt", title: "Emergency Service", desc: "24/7 emergency dispatch. If your system goes down, we'll be there fast — day or night." },
    { icon: "fa-clipboard-check", title: "Preventive Maintenance", desc: "Annual service agreements to keep your systems running efficiently all year long." },
    { icon: "fa-building", title: "Commercial HVAC", desc: "Rooftop units, split systems, and building controls for commercial properties of all sizes." },
  ],
  5: [
    { icon: "fa-fire", title: "Emergency Heating", desc: "No heat in winter? We dispatch immediately, day or night, every day of the year." },
    { icon: "fa-icicles", title: "Emergency Cooling", desc: "AC failure in summer? Fast-response repair when you need cool air the most." },
    { icon: "fa-clock", title: "24/7 Dispatch", desc: "Technicians on call every hour. No extra charge for nights, weekends, or holidays." },
    { icon: "fa-certificate", title: "All Makes & Models", desc: "We service and repair every brand of heating and cooling equipment, no exclusions." },
    { icon: "fa-money-bill-wave", title: "Flat-Rate Pricing", desc: "Know exactly what you'll pay before we start. No surprise bills, no bait-and-switch." },
    { icon: "fa-shield-halved", title: "Full Maintenance", desc: "Annual tune-ups and cleaning to prevent breakdowns before they happen." },
  ],
};

function buildHVACServiceCardsT4(services) {
  return services.map((s, i) => `
    <a href="#contact" class="srv-card anim d${i + 1}">
      <div class="srv-icon"><i class="fa-solid ${s.icon}"></i></div>
      <h3>${s.title}</h3>
      <p>${s.desc}</p>
      <span class="srv-card-link">Schedule Service <i class="fa-solid fa-arrow-right"></i></span>
    </a>`).join('');
}

function buildHVACServiceCardsT5(services) {
  return services.map((s, i) => `
    <a href="#contact" class="srv-card anim d${i + 1}">
      <div class="srv-icon"><i class="fa-solid ${s.icon}" aria-hidden="true"></i></div>
      <h3>${s.title}</h3>
      <p>${s.desc}</p>
      <span class="srv-link">Schedule <i class="fa-solid fa-arrow-right" aria-hidden="true"></i></span>
    </a>`).join('');
}

// ── T4: Climate Pro ───────────────────────────────────────────────────────────
export const T4 = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="robots" content="noindex,nofollow">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <title>PLACEHOLDER_COMPANY | Heating &amp; Cooling</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="preconnect" href="https://cdnjs.cloudflare.com">
  <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Manrope:wght@400;500;600;700&display=swap" rel="stylesheet">
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css">
  <style>
    :root {
      --navy: #0d1b3e; --navy-mid: #162040; --navy-light: #1e2f55;
      --blue: #38bdf8; --blue-mid: #0ea5e9; --blue-deep: #0369a1;
      --cream: #f0f7fc; --white: #ffffff; --off-white: #f7f9fb;
      --border: #dce8f2; --border-warm: #d0e4f0;
      --text: #0d1b3e; --text-mid: #3a4a6b; --text-muted: #6b7a9a; --text-light: #9daabb;
      --gold: #f5a623; --gold-light: #fef3d8;
      --container: 1160px;
    }
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    body { font-family: 'Manrope', sans-serif; background: var(--white); color: var(--text); overflow-x: hidden; }
    img { display: block; max-width: 100%; }
    h1,h2,h3 { text-wrap: balance; }
    #services, #about, #testimonials, #contact { scroll-margin-top: 104px; }
    .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
    .container { max-width: var(--container); margin: 0 auto; padding: 0 48px; }

    /* TOPBAR */
    .topbar { position: fixed; top: 0; left: 0; right: 0; z-index: 300; height: 36px; background: var(--navy); display: flex; align-items: center; }
    .topbar-inner { max-width: var(--container); margin: 0 auto; padding: 0 40px; display: flex; align-items: center; justify-content: space-between; width: 100%; }
    .topbar-left { display: flex; align-items: center; gap: 20px; }
    .topbar-item { display: flex; align-items: center; gap: 6px; font-family: 'Outfit', sans-serif; font-size: 11px; font-weight: 500; color: rgba(255,255,255,0.65); text-decoration: none; }
    .topbar-item i { font-size: 10px; color: var(--blue); }
    .topbar-social { display: flex; align-items: center; gap: 12px; }
    .topbar-social a { font-size: 12px; color: rgba(255,255,255,0.45); text-decoration: none; transition: color 0.2s; }
    .topbar-social a:hover { color: var(--blue); }

    /* NAV */
    nav { position: fixed; top: 36px; left: 0; right: 0; z-index: 200; height: 68px; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; padding: 0 40px; background: rgba(255,255,255,0.97); backdrop-filter: blur(20px); border-bottom: 1px solid var(--border); box-shadow: 0 2px 12px rgba(13,27,62,0.06); }
    .nav-logo { display: flex; align-items: center; gap: 11px; text-decoration: none; justify-self: start; }
    .nav-logo-icon { width: 34px; height: 34px; background: var(--navy); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: var(--blue); font-size: 14px; }
    .nav-logo-text { font-family: 'Outfit', sans-serif; font-size: 17px; font-weight: 800; color: var(--navy); letter-spacing: -0.02em; }
    .nav-links { display: flex; gap: 36px; list-style: none; justify-self: center; }
    .nav-links a { font-family: 'Manrope', sans-serif; font-size: 13px; font-weight: 600; color: var(--text-mid); text-decoration: none; transition: color 0.2s; }
    .nav-links a:hover { color: var(--navy); }
    .nav-right { display: flex; align-items: center; gap: 16px; justify-self: end; }
    .nav-phone { font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 700; color: var(--navy); text-decoration: none; display: flex; align-items: center; gap: 7px; }
    .nav-phone i { font-size: 12px; color: var(--blue-mid); }
    .nav-cta { background: var(--navy); color: var(--white); font-family: 'Outfit', sans-serif; font-size: 13px; font-weight: 700; letter-spacing: 0.02em; padding: 9px 20px; border-radius: 5px; text-decoration: none; white-space: nowrap; transition: background 0.2s, transform 0.15s; }
    .nav-cta:hover { background: var(--navy-light); transform: translateY(-1px); }

    /* HERO ANIMATIONS */
    @keyframes heroUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
    .hero-h1      { animation: heroUp 0.75s cubic-bezier(0.22,1,0.36,1) both 0.20s; }
    .hero-tagline { animation: heroUp 0.75s cubic-bezier(0.22,1,0.36,1) both 0.32s; }
    .hero-form    { animation: heroUp 0.85s cubic-bezier(0.22,1,0.36,1) both 0.30s; }
    @media (prefers-reduced-motion: reduce) { .hero-h1,.hero-tagline,.hero-form{animation:none} }

    /* HERO */
    .hero { margin-top: 104px; position: relative; overflow: hidden; min-height: 68vh; display: flex; align-items: center; }
    .hero-bg { position: absolute; inset: 0; background-image: url('PLACEHOLDER_IMG/pexels-jose-andres-pacheco-cortes-3641213-5463575.jpg'); background-size: cover; background-position: center center; filter: brightness(0.72); }
    .hero-overlay { position: absolute; inset: 0; background: linear-gradient(105deg, rgba(13,27,62,0.78) 0%, rgba(13,27,62,0.60) 42%, rgba(13,27,62,0.28) 100%); }
    .hero-inner-wrap { position: relative; z-index: 1; max-width: var(--container); margin: 0 auto; padding: 64px 48px; display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: center; width: 100%; }
    .hero-left { display: flex; flex-direction: column; }
    .hero-inner { max-width: 480px; }

    .hero-h1 { font-family: 'Outfit', sans-serif; font-size: clamp(40px,4.6vw,66px); font-weight: 900; line-height: 1.08; letter-spacing: -0.04em; color: var(--white); margin-bottom: 16px; }
    .hero-h1 em { color: var(--blue); font-style: normal; }
    .hero-tagline { font-size: 16px; color: rgba(255,255,255,0.82); line-height: 1.8; margin-bottom: 28px; max-width: 440px; }
    .hero-trust { display: flex; flex-direction: column; gap: 10px; margin-bottom: 32px; list-style: none; }
    .hero-trust-item { display: flex; align-items: center; gap: 10px; font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.9); }
    .hero-trust-item i { color: var(--blue); font-size: 12px; width: 14px; text-align: center; }
    .hero-btns { display: flex; gap: 12px; flex-wrap: wrap; }
    .btn-primary { background: var(--white); color: var(--navy); font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 700; padding: 13px 26px; border-radius: 5px; text-decoration: none; display: inline-flex; align-items: center; gap: 9px; transition: background 0.2s, transform 0.15s; }
    .btn-primary:hover { background: var(--cream); transform: translateY(-1px); }
    .btn-secondary { background: transparent; color: var(--white); font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 700; padding: 13px 26px; border-radius: 5px; border: 2px solid rgba(255,255,255,0.35); text-decoration: none; display: inline-flex; align-items: center; gap: 9px; transition: border-color 0.2s; }
    .btn-secondary:hover { border-color: rgba(255,255,255,0.75); }

    /* HERO FORM CARD */
    .hero-right { display: flex; align-items: center; justify-content: flex-end; }
    .hero-form-card { background: var(--white); border-radius: 14px; padding: 40px 36px; width: 100%; max-width: 460px; box-shadow: 0 20px 60px rgba(0,0,0,0.28), 0 4px 16px rgba(0,0,0,0.14); }
    .hfc-header { margin-bottom: 24px; }
    .hfc-title { font-family: 'Outfit', sans-serif; font-size: 18px; font-weight: 800; color: var(--navy); letter-spacing: -0.02em; }
    .hfc-fields { display: flex; flex-direction: column; gap: 14px; }
    .hfc-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .hfc-group { display: flex; flex-direction: column; gap: 5px; }
    .hfc-label { font-family: 'Outfit', sans-serif; font-size: 11px; font-weight: 700; color: var(--text-mid); text-transform: uppercase; letter-spacing: 0.1em; }
    .hfc-input, .hfc-textarea { width: 100%; background: var(--off-white); border: 1.5px solid var(--border); border-radius: 6px; padding: 11px 13px; font-family: 'Manrope', sans-serif; font-size: 13px; color: var(--text); outline: none; transition: border-color 0.2s, box-shadow 0.2s; }
    .hfc-input::placeholder, .hfc-textarea::placeholder { color: var(--text-light); }
    .hfc-input:focus, .hfc-textarea:focus { border-color: var(--blue-mid); box-shadow: 0 0 0 3px rgba(14,165,233,0.12); background: var(--white); }
    .hfc-textarea { resize: vertical; min-height: 96px; }
    .hfc-submit { background: var(--navy); color: var(--white); border: none; border-radius: 7px; padding: 15px 24px; width: 100%; margin-top: 4px; font-family: 'Outfit', sans-serif; font-size: 15px; font-weight: 800; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; box-shadow: 0 4px 14px rgba(13,27,62,0.22); transition: background 0.2s, transform 0.15s; }
    .hfc-submit:hover { background: var(--navy-light); transform: translateY(-1px); }

    /* TRUST BAR */
    .trust-bar { background: var(--white); border-bottom: 1px solid var(--border); padding: 14px 48px; }
    .trust-bar-inner { max-width: var(--container); margin: 0 auto; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; }
    .trust-item { display: flex; align-items: center; gap: 7px; font-family: 'Outfit', sans-serif; font-size: 11px; font-weight: 700; color: var(--text-mid); letter-spacing: 0.08em; text-transform: uppercase; }
    .trust-item i { color: var(--blue-mid); font-size: 11px; }

    /* SECTION HELPERS */
    .sec-pill { display: inline-flex; align-items: center; justify-content: center; background: var(--gold-light); border: 1px solid rgba(245,166,35,0.28); border-radius: 100px; padding: 4px 14px; font-family: 'Outfit', sans-serif; font-size: 10px; font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase; color: #9a6b00; margin-bottom: 14px; }
    .sec-pill.blue { background: rgba(14,165,233,0.08); border-color: rgba(14,165,233,0.2); color: var(--blue-deep); }
    .sec-title { font-family: 'Outfit', sans-serif; font-size: clamp(28px,3.6vw,48px); font-weight: 900; color: var(--navy); letter-spacing: -0.04em; line-height: 1.06; margin-bottom: 14px; }
    .sec-title em { color: var(--blue-mid); font-style: normal; }
    .sec-sub { font-size: 15px; color: var(--text-muted); line-height: 1.75; max-width: 520px; }

    /* SERVICES */
    .services { background: var(--white); padding: 88px 0; border-bottom: 1px solid var(--border); }
    .services .sec-header { text-align: center; margin-bottom: 52px; }
    .services .sec-header .sec-sub { margin: 0 auto; }
    .services-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); border-radius: 10px; overflow: hidden; }
    .srv-card { display: flex; flex-direction: column; gap: 10px; background: var(--white); padding: 34px 26px; text-decoration: none; color: inherit; position: relative; overflow: hidden; transition: background 0.25s, transform 0.25s; }
    .srv-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, var(--blue-mid), var(--blue)); transform: scaleX(0); transform-origin: left; transition: transform 0.35s ease; }
    .srv-card:hover { background: var(--cream); transform: translateY(-2px); z-index: 2; }
    .srv-card:hover::before { transform: scaleX(1); }
    .srv-icon { width: 46px; height: 46px; background: rgba(14,165,233,0.08); border: 1px solid rgba(14,165,233,0.18); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: var(--blue-mid); font-size: 18px; transition: background 0.2s, box-shadow 0.2s; }
    .srv-card:hover .srv-icon { background: rgba(14,165,233,0.14); box-shadow: 0 0 14px rgba(14,165,233,0.18); }
    .srv-card h3 { font-family: 'Outfit', sans-serif; font-size: 15px; font-weight: 700; color: var(--navy); }
    .srv-card p { font-size: 13px; color: var(--text-muted); line-height: 1.7; flex: 1; }
    .srv-card-link { font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 700; color: var(--blue-deep); letter-spacing: 0.03em; display: flex; align-items: center; gap: 6px; margin-top: 6px; transition: gap 0.2s; }
    .srv-card:hover .srv-card-link { gap: 10px; }

    /* PHOTO WALL */
    .photo-wall { display: grid; grid-template-columns: 1.4fr 0.8fr 1fr; height: 340px; gap: 3px; }
    .pw-cell { overflow: hidden; position: relative; }
    .pw-cell img { width: 100%; height: 100%; object-fit: cover; display: block; filter: brightness(0.88) saturate(0.85); }
    .pw-cell-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(13,27,62,0.38) 0%, transparent 55%); pointer-events: none; }

    /* ABOUT */
    .about { background: var(--cream); padding: 96px 0; border-top: 1px solid var(--border-warm); border-bottom: 1px solid var(--border-warm); }
    .about-inner { max-width: var(--container); margin: 0 auto; padding: 0 48px; display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
    .about-photos { display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; gap: 10px; height: 440px; }
    .about-photo-main { grid-row: 1 / 3; overflow: hidden; border-radius: 10px; box-shadow: 0 4px 20px rgba(13,27,62,0.10); }
    .about-photo-main img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .about-photo-sm { overflow: hidden; border-radius: 10px; box-shadow: 0 4px 16px rgba(13,27,62,0.08); }
    .about-photo-sm img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .about-content .sec-sub { margin-bottom: 36px; }
    .about-feats { display: flex; flex-direction: column; gap: 20px; margin-bottom: 36px; }
    .about-feat { display: flex; align-items: flex-start; gap: 16px; }
    .feat-icon { width: 44px; height: 44px; background: var(--white); border: 1.5px solid var(--border); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: var(--blue-mid); font-size: 16px; flex-shrink: 0; margin-top: 2px; box-shadow: 0 2px 8px rgba(13,27,62,0.06); }
    .feat-text h4 { font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 700; color: var(--navy); margin-bottom: 3px; }
    .feat-text p { font-size: 13px; color: var(--text-muted); line-height: 1.6; }
    .btn-dark { background: var(--navy); color: var(--white); font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 700; padding: 13px 26px; border-radius: 5px; text-decoration: none; display: inline-flex; align-items: center; gap: 9px; transition: background 0.2s, transform 0.15s; }
    .btn-dark:hover { background: var(--navy-light); transform: translateY(-1px); }

    /* STATS */
    .stats-strip { background: var(--navy); padding: 64px 0; position: relative; overflow: hidden; }
    .stats-strip::before { content: ''; position: absolute; inset: 0; pointer-events: none; background-image: repeating-linear-gradient(-48deg, rgba(56,189,248,0.04) 0, rgba(56,189,248,0.04) 1px, transparent 1px, transparent 28px); }
    .stats-inner { max-width: var(--container); margin: 0 auto; padding: 0 48px; display: grid; grid-template-columns: repeat(4,1fr); gap: 20px; text-align: center; position: relative; z-index: 1; }
    .stat-item { padding: 0 16px; border-right: 1px solid rgba(255,255,255,0.08); }
    .stat-item:last-child { border-right: none; }
    .stat-num { font-family: 'Outfit', sans-serif; font-size: 52px; font-weight: 900; color: var(--white); line-height: 1; letter-spacing: -0.04em; display: block; }
    .stat-label { font-family: 'Outfit', sans-serif; font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.45); margin-top: 8px; letter-spacing: 0.08em; text-transform: uppercase; }

    /* TESTIMONIALS */
    .testimonials { background: var(--white); padding: 96px 0; border-bottom: 1px solid var(--border); }
    .testimonials .sec-header { text-align: center; margin-bottom: 52px; }
    .testimonials .sec-header .sec-sub { margin: 0 auto; }
    .testi-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 24px; }
    .testi-card { background: var(--cream); border: 1px solid var(--border-warm); border-radius: 12px; padding: 32px 28px; display: flex; flex-direction: column; gap: 16px; transition: box-shadow 0.25s, transform 0.25s; }
    .testi-card:hover { box-shadow: 0 8px 30px rgba(13,27,62,0.09); transform: translateY(-3px); }
    .testi-stars { display: flex; gap: 4px; }
    .testi-stars i { color: var(--gold); font-size: 13px; }
    .testi-quote { font-size: 14px; color: var(--text-mid); line-height: 1.75; flex: 1; font-style: italic; }
    .testi-author { display: flex; align-items: center; gap: 12px; }
    .testi-avatar { width: 44px; height: 44px; border-radius: 50%; background: var(--navy); color: var(--white); font-family: 'Outfit', sans-serif; font-size: 15px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .testi-name { font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 700; color: var(--navy); }
    .testi-loc { font-size: 12px; color: var(--text-muted); }

    /* CTA BANNER */
    .cta-banner { background: var(--navy-mid); padding: 56px 0; position: relative; overflow: hidden; }
    .cta-banner::before { content: ''; position: absolute; right: -80px; top: 50%; transform: translateY(-50%); width: 500px; height: 500px; pointer-events: none; background: radial-gradient(circle, rgba(56,189,248,0.08) 0%, transparent 65%); }
    .cta-banner-inner { max-width: var(--container); margin: 0 auto; padding: 0 48px; display: flex; align-items: center; justify-content: space-between; gap: 40px; position: relative; z-index: 1; }
    .cta-banner-left h2 { font-family: 'Outfit', sans-serif; font-size: clamp(24px,3vw,38px); font-weight: 900; color: var(--white); letter-spacing: -0.03em; line-height: 1.15; margin-bottom: 8px; }
    .cta-banner-left p { font-size: 14px; color: rgba(255,255,255,0.6); }
    .cta-banner-right { display: flex; align-items: center; gap: 32px; flex-shrink: 0; }
    .cta-contact-item small { display: block; font-family: 'Outfit', sans-serif; font-size: 10px; font-weight: 600; color: var(--blue); letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 4px; }
    .cta-contact-item a { font-family: 'Outfit', sans-serif; font-size: 20px; font-weight: 800; color: var(--white); text-decoration: none; letter-spacing: -0.02em; }
    .cta-contact-item a:hover { color: var(--blue); }
    .btn-cta { background: var(--blue-mid); color: var(--white); font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 700; padding: 14px 28px; border-radius: 5px; text-decoration: none; display: inline-flex; align-items: center; gap: 9px; white-space: nowrap; transition: background 0.2s, transform 0.15s; }
    .btn-cta:hover { background: var(--blue-deep); transform: translateY(-1px); }

    /* CONTACT */
    .contact { background: var(--off-white); padding: 96px 0; border-top: 1px solid var(--border); }
    .contact-inner { max-width: var(--container); margin: 0 auto; padding: 0 48px; display: grid; grid-template-columns: 1fr 1.4fr; gap: 80px; align-items: start; }
    .contact-left .sec-title { font-size: clamp(26px,3vw,40px); margin-bottom: 12px; }
    .contact-left > p { font-size: 14px; color: var(--text-muted); line-height: 1.75; margin-bottom: 36px; }
    .contact-info { display: flex; flex-direction: column; gap: 16px; }
    .ci-row { display: flex; align-items: center; gap: 14px; }
    .ci-icon { width: 42px; height: 42px; background: var(--white); border: 1.5px solid var(--border); border-radius: 9px; display: flex; align-items: center; justify-content: center; color: var(--blue-mid); font-size: 15px; flex-shrink: 0; box-shadow: 0 2px 8px rgba(13,27,62,0.05); }
    .ci-text { font-family: 'Outfit', sans-serif; font-size: 15px; font-weight: 700; color: var(--navy); }
    .ci-text span { display: block; font-size: 10px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 2px; }
    .contact-form { background: var(--white); border: 1px solid var(--border); border-radius: 12px; padding: 40px; display: flex; flex-direction: column; gap: 16px; box-shadow: 0 8px 32px rgba(13,27,62,0.07); }
    .cf-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
    .cf-label { font-family: 'Outfit', sans-serif; font-size: 11px; font-weight: 700; color: var(--text-mid); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 6px; display: block; }
    .cf-input, .cf-select, .cf-textarea { width: 100%; background: var(--off-white); border: 1.5px solid var(--border); border-radius: 5px; padding: 12px 14px; font-family: 'Manrope', sans-serif; font-size: 14px; color: var(--text); outline: none; transition: border-color 0.2s, background 0.2s; }
    .cf-input::placeholder, .cf-textarea::placeholder { color: var(--text-light); }
    .cf-input:focus, .cf-select:focus, .cf-textarea:focus { border-color: var(--blue-mid); background: var(--white); }
    .cf-select { appearance: none; cursor: pointer; color: var(--text-muted); }
    .cf-select.has-value { color: var(--text); }
    .cf-textarea { resize: vertical; min-height: 100px; }
    .cf-submit { background: var(--navy); color: var(--white); border: none; border-radius: 5px; padding: 15px 28px; font-family: 'Outfit', sans-serif; font-size: 15px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; margin-top: 4px; transition: background 0.2s, transform 0.15s; }
    .cf-submit:hover { background: var(--navy-light); transform: translateY(-1px); }

    /* FOOTER */
    footer { background: var(--navy); border-top: 1px solid rgba(255,255,255,0.06); padding: 64px 0 40px; }
    .footer-inner { max-width: var(--container); margin: 0 auto; padding: 0 48px; }
    .footer-grid { display: grid; grid-template-columns: 1.4fr 1fr 1fr 1fr; gap: 48px; margin-bottom: 48px; }
    .footer-brand .footer-logo-text { font-family: 'Outfit', sans-serif; font-size: 17px; font-weight: 800; color: var(--white); letter-spacing: -0.02em; margin-bottom: 12px; display: block; }
    .footer-brand > p { font-size: 13px; color: rgba(255,255,255,0.42); line-height: 1.75; margin-bottom: 20px; }
    .footer-social { display: flex; gap: 8px; }
    .social-btn { width: 34px; height: 34px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.1); border-radius: 7px; display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,0.4); font-size: 13px; text-decoration: none; transition: background 0.2s, color 0.2s, border-color 0.2s; }
    .social-btn:hover { background: var(--blue-mid); color: var(--white); border-color: var(--blue-mid); }
    .footer-col h4 { font-family: 'Outfit', sans-serif; font-size: 11px; font-weight: 700; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 0.12em; margin-bottom: 16px; }
    .footer-col ul { list-style: none; display: flex; flex-direction: column; gap: 9px; }
    .footer-col ul li { font-size: 13px; color: rgba(255,255,255,0.42); }
    .footer-col a { color: rgba(255,255,255,0.42); text-decoration: none; transition: color 0.2s; }
    .footer-col a:hover { color: var(--blue); }
    .footer-bottom { border-top: 1px solid rgba(255,255,255,0.07); padding-top: 24px; display: flex; justify-content: space-between; align-items: center; }
    .footer-bottom p { font-size: 12px; color: rgba(255,255,255,0.28); }

    /* SCROLL ANIMATIONS */
    .anim { opacity: 0; transform: translateY(28px); transition: opacity 0.65s ease, transform 0.65s ease; }
    .anim.left { transform: translateX(-28px); }
    .anim.right { transform: translateX(28px); }
    .anim.on { opacity: 1; transform: none; }
    .d1{transition-delay:.06s} .d2{transition-delay:.13s} .d3{transition-delay:.20s}
    .d4{transition-delay:.27s} .d5{transition-delay:.34s} .d6{transition-delay:.41s}
    @media (prefers-reduced-motion: reduce) { .anim{opacity:1;transform:none;transition:none} }

    @media (max-width: 1024px) {
      .topbar-left span:last-child { display: none; }
      .hero-inner-wrap { grid-template-columns: 1fr; padding: 48px 40px; }
      .hero-left { align-items: center; text-align: center; }
      .hero-inner { max-width: 600px; margin: 0 auto; }
      .hero-tagline { margin-left: auto; margin-right: auto; }
      .hero-trust { align-items: center; }
      .hero-btns { justify-content: center; }
      .hero-right { justify-content: center; }
      .hero-form-card { max-width: 100%; }
      .photo-wall { grid-template-columns: 1fr 1fr; height: 280px; }
      .photo-wall .pw-cell:last-child { display: none; }
      .services-grid { grid-template-columns: repeat(2,1fr); }
      .about-inner { grid-template-columns: 1fr; gap: 48px; }
      .testi-grid { grid-template-columns: 1fr 1fr; }
      .cta-banner-inner { flex-direction: column; align-items: flex-start; gap: 28px; }
      .contact-inner { grid-template-columns: 1fr; gap: 48px; }
      .footer-grid { grid-template-columns: 1fr 1fr; gap: 32px; }
      .stats-inner { grid-template-columns: 1fr 1fr; }
    }
    @media (max-width: 768px) {
      .topbar { display: none; }
      nav { top: 0; }
      #services, #about, #testimonials, #contact { scroll-margin-top: 68px; }
      .hero { margin-top: 68px; }
      nav { padding: 0 20px; height: 60px; grid-template-columns: auto 1fr auto; }
      .nav-links { display: none; }
      .nav-phone { display: none; }
      .hero-inner-wrap { grid-template-columns: 1fr; padding: 40px 24px; gap: 32px; }
      .hfc-row { grid-template-columns: 1fr; }
      .trust-bar { padding: 12px 20px; }
      .trust-bar-inner { gap: 10px 20px; }
      .trust-item { flex: 0 0 calc(50% - 10px); }
      .container { padding: 0 24px; }
      .services { padding: 60px 0; }
      .services-grid { grid-template-columns: 1fr; }
      .photo-wall { height: 200px; grid-template-columns: 1fr 1fr; }
      .photo-wall .pw-cell:last-child { display: none; }
      .stats-strip { padding: 44px 0; }
      .stats-inner { grid-template-columns: 1fr 1fr; padding: 0 24px; }
      .stat-num { font-size: 40px; }
      .about { padding: 60px 0; }
      .about-inner { grid-template-columns: 1fr; padding: 0 24px; }
      .about-photos { height: 240px; }
      .about-photo-sm { display: none; }
      .about-photo-main { grid-row: auto; }
      .testimonials { padding: 60px 0; }
      .testi-grid { grid-template-columns: 1fr; }
      .cta-banner { padding: 40px 0; }
      .cta-banner-inner { padding: 0 24px; flex-direction: column; }
      .contact { padding: 60px 0; }
      .contact-inner { padding: 0 24px; grid-template-columns: 1fr; }
      .contact-form { padding: 28px 20px; }
      .cf-row { grid-template-columns: 1fr; }
      footer { padding: 48px 0 32px; }
      .footer-inner { padding: 0 24px; }
      .footer-grid { grid-template-columns: 1fr; gap: 28px; }
      .footer-bottom { flex-direction: column; gap: 8px; text-align: center; }
    }
  </style>
</head>
<body>

<div class="topbar">
  <div class="topbar-inner">
    <div class="topbar-left">
      <a href="mailto:PLACEHOLDER_EMAIL" class="topbar-item"><i class="fa-solid fa-envelope"></i>PLACEHOLDER_EMAIL</a>
      <span class="topbar-item"><i class="fa-solid fa-location-dot"></i>PLACEHOLDER_ADDRESS</span>
    </div>
    <div class="topbar-social">
      <a href="#"><i class="fa-brands fa-facebook-f"></i></a>
      <a href="#"><i class="fa-brands fa-instagram"></i></a>
      <a href="#"><i class="fa-brands fa-google"></i></a>
      <a href="#"><i class="fa-brands fa-yelp"></i></a>
    </div>
  </div>
</div>

<nav>
  PLACEHOLDER_NAV_LOGO_T4
  <ul class="nav-links">
    <li><a href="#services">Services</a></li>
    <li><a href="#about">About</a></li>
    <li><a href="#testimonials">Reviews</a></li>
    <li><a href="#contact">Contact</a></li>
  </ul>
  <div class="nav-right">
    <a href="tel:PLACEHOLDER_PHONE_DIGITS" class="nav-phone"><i class="fa-solid fa-phone"></i>PLACEHOLDER_PHONE</a>
    <a href="#contact" class="nav-cta">Get Free Quote</a>
  </div>
</nav>

<section class="hero" id="main">
  <div class="hero-bg"></div>
  <div class="hero-overlay"></div>
  <div class="hero-inner-wrap">
    <div class="hero-left">
      <div class="hero-inner">
        <h1 class="hero-h1">PLACEHOLDER_HERO_H1</h1>
        <p class="hero-tagline">PLACEHOLDER_HERO_TAGLINE</p>
        <ul class="hero-trust">
          <li class="hero-trust-item"><i class="fa-solid fa-check"></i> Licensed, bonded and fully insured</li>
          <li class="hero-trust-item"><i class="fa-solid fa-check"></i> Same-day and 24/7 emergency service</li>
          <li class="hero-trust-item"><i class="fa-solid fa-check"></i> Free estimates, no hidden fees</li>
          <li class="hero-trust-item"><i class="fa-solid fa-check"></i> 5-star rated, 2,400+ jobs completed</li>
        </ul>
        <div class="hero-btns">
          <a href="tel:PLACEHOLDER_PHONE_DIGITS" class="btn-primary"><i class="fa-solid fa-phone"></i> Call PLACEHOLDER_PHONE</a>
          <a href="#services" class="btn-secondary">See All Services <i class="fa-solid fa-arrow-right"></i></a>
        </div>
      </div>
    </div>
    <div class="hero-right">
      <div class="hero-form-card hero-form">
        <div class="hfc-header">
          <div class="hfc-title">Get a Free Estimate</div>
        </div>
        <div class="hfc-fields">
          <div class="hfc-row">
            <div class="hfc-group">
              <label class="hfc-label" for="hfc-fname">First Name</label>
              <input class="hfc-input" type="text" id="hfc-fname" placeholder="First Name">
            </div>
            <div class="hfc-group">
              <label class="hfc-label" for="hfc-lname">Last Name</label>
              <input class="hfc-input" type="text" id="hfc-lname" placeholder="Last Name">
            </div>
          </div>
          <div class="hfc-row">
            <div class="hfc-group">
              <label class="hfc-label" for="hfc-email">Email *</label>
              <input class="hfc-input" type="email" id="hfc-email" placeholder="Email">
            </div>
            <div class="hfc-group">
              <label class="hfc-label" for="hfc-phone">Phone *</label>
              <input class="hfc-input" type="tel" id="hfc-phone" placeholder="Phone">
            </div>
          </div>
          <div class="hfc-group">
            <label class="hfc-label" for="hfc-message">Message here</label>
            <textarea class="hfc-textarea" id="hfc-message" placeholder="Describe your issue..."></textarea>
          </div>
          <button class="hfc-submit" type="button">Submit</button>
        </div>
      </div>
    </div>
  </div>
</section>

<div class="trust-bar">
  <div class="trust-bar-inner">
    <div class="trust-item"><i class="fa-solid fa-shield-halved"></i>Licensed &amp; Insured</div>
    <div class="trust-item"><i class="fa-solid fa-bolt"></i>Same-Day Service</div>
    <div class="trust-item"><i class="fa-solid fa-wrench"></i>All Makes &amp; Models</div>
    <div class="trust-item"><i class="fa-solid fa-tag"></i>Free Estimates</div>
    <div class="trust-item"><i class="fa-solid fa-clock"></i>24/7 Emergency Line</div>
    <div class="trust-item"><i class="fa-solid fa-star"></i>5-Star Rated</div>
    <div class="trust-item"><i class="fa-solid fa-dollar-sign"></i>Upfront Pricing</div>
  </div>
</div>

<section class="services" id="services">
  <div class="container">
    <div class="sec-header anim">
      <div class="sec-pill">Services</div>
      <h2 class="sec-title">Full-Service HVAC <em>Solutions</em></h2>
      <p class="sec-sub">From emergency repairs to full system installs, we handle every aspect of your home comfort.</p>
    </div>
    <div class="services-grid">PLACEHOLDER_SERVICE_CARDS_T4</div>
  </div>
</section>

<div class="photo-wall">
  <div class="pw-cell"><img src="PLACEHOLDER_IMG/pexels-artbovich-7534232.jpg" alt=""><div class="pw-cell-overlay"></div></div>
  <div class="pw-cell"><img src="PLACEHOLDER_IMG/compagnons-yZmHFF-g-W0-unsplash.jpg" alt=""><div class="pw-cell-overlay"></div></div>
  <div class="pw-cell"><img src="PLACEHOLDER_IMG/pexels-optical-chemist-340351297-36360393.jpg" alt=""><div class="pw-cell-overlay"></div></div>
</div>

<section class="about" id="about">
  <div class="about-inner">
    <div class="about-photos anim left">
      <div class="about-photo-main"><img src="PLACEHOLDER_IMG/pexels-pixabay-257770.jpg" alt=""></div>
      <div class="about-photo-sm"><img src="PLACEHOLDER_IMG/pexels-soc-nang-d-ng-2150345854-36248286.jpg" alt=""></div>
      <div class="about-photo-sm"><img src="PLACEHOLDER_IMG/pexels-optical-chemist-340351297-36360393.jpg" alt=""></div>
    </div>
    <div class="about-content anim right">
      <div class="sec-pill">Why Choose Us</div>
      <h2 class="sec-title">Local Experts<br>You Can <em>Trust</em></h2>
      <p class="sec-sub">PLACEHOLDER_ABOUT_PARA</p>
      <div class="about-feats">
        <div class="about-feat"><div class="feat-icon"><i class="fa-solid fa-certificate"></i></div><div class="feat-text"><h4>Licensed, Bonded and Fully Insured</h4><p>Ohio contractor license, full liability, and workers comp on every single job.</p></div></div>
        <div class="about-feat"><div class="feat-icon"><i class="fa-solid fa-dollar-sign"></i></div><div class="feat-text"><h4>Upfront Pricing, No Surprises</h4><p>You will know the exact cost before we touch anything. No bait-and-switch, no hidden fees.</p></div></div>
        <div class="about-feat"><div class="feat-icon"><i class="fa-solid fa-clock"></i></div><div class="feat-text"><h4>Same-Day Service Available</h4><p>Most calls are handled same day. Emergency technicians on call 24 hours a day.</p></div></div>
        <div class="about-feat"><div class="feat-icon"><i class="fa-solid fa-star"></i></div><div class="feat-text"><h4>5-Star Rated on Google and Yelp</h4><p>Hundreds of reviews from real homeowners and business owners in the area.</p></div></div>
      </div>
      <a href="#contact" class="btn-dark"><i class="fa-solid fa-calendar-check"></i> Get Your Free Estimate</a>
    </div>
  </div>
</section>

<div class="stats-strip">
  <div class="stats-inner">
    <div class="stat-item anim"><span class="stat-num">24/7</span><div class="stat-label">Emergency Service</div></div>
    <div class="stat-item anim d1"><span class="stat-num">15+</span><div class="stat-label">Years in Business</div></div>
    <div class="stat-item anim d2"><span class="stat-num">2,400+</span><div class="stat-label">Systems Serviced</div></div>
    <div class="stat-item anim d3"><span class="stat-num">100%</span><div class="stat-label">Satisfaction Guarantee</div></div>
  </div>
</div>

<section class="testimonials" id="testimonials">
  <div class="container">
    <div class="sec-header anim">
      <div class="sec-pill">Testimonials</div>
      <h2 class="sec-title">Real Stories from <em>Our Clients</em></h2>
      <p class="sec-sub">Trusted by homeowners for dependable HVAC service, comfort, and care at every step.</p>
    </div>
    <div class="testi-grid">
      <div class="testi-card anim d1">
        <div class="testi-stars"><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i></div>
        <p class="testi-quote">"My furnace went out on the coldest night of the year and they had a tech at my door within two hours. Fixed it same night, no upsell, no runaround. These guys are the real deal."</p>
        <div class="testi-author"><div class="testi-avatar">MK</div><div><div class="testi-name">Mark K.</div><div class="testi-loc">Local Homeowner</div></div></div>
      </div>
      <div class="testi-card anim d2">
        <div class="testi-stars"><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i></div>
        <p class="testi-quote">"Had a new central AC installed last spring. Price was exactly what they quoted, the team was respectful of our home, and the system has been flawless. Highly recommend."</p>
        <div class="testi-author"><div class="testi-avatar">SL</div><div><div class="testi-name">Sarah L.</div><div class="testi-loc">Local Homeowner</div></div></div>
      </div>
      <div class="testi-card anim d3">
        <div class="testi-stars"><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i><i class="fa-solid fa-star"></i></div>
        <p class="testi-quote">"We use them for our office building maintenance contract. Always on time, always thorough. Our HVAC systems have never run better."</p>
        <div class="testi-author"><div class="testi-avatar">DT</div><div><div class="testi-name">Dave T.</div><div class="testi-loc">Office Manager</div></div></div>
      </div>
    </div>
  </div>
</section>

<div class="cta-banner">
  <div class="cta-banner-inner">
    <div class="cta-banner-left anim left">
      <h2>Get a Free Estimate<br>for Your Next Project</h2>
      <p>Same-day appointments available. No obligation, no pressure.</p>
    </div>
    <div class="cta-banner-right anim right">
      <div class="cta-contact-item"><small>Call us</small><a href="tel:PLACEHOLDER_PHONE_DIGITS">PLACEHOLDER_PHONE</a></div>
      <a href="#contact" class="btn-cta">Get Free Quote <i class="fa-solid fa-arrow-right"></i></a>
    </div>
  </div>
</div>

<section class="contact" id="contact">
  <div class="contact-inner">
    <div class="contact-left anim left">
      <div class="sec-pill blue">Get in Touch</div>
      <h2 class="sec-title">Ready for Year-Round <em>Comfort?</em></h2>
      <p>Call or fill out the form and we will get back to you fast. Same-day service available.</p>
      <div class="contact-info">
        <div class="ci-row"><div class="ci-icon"><i class="fa-solid fa-phone"></i></div><div class="ci-text"><span>Phone</span>PLACEHOLDER_PHONE</div></div>
        <div class="ci-row"><div class="ci-icon"><i class="fa-solid fa-envelope"></i></div><div class="ci-text"><span>Email</span>PLACEHOLDER_EMAIL</div></div>
        <div class="ci-row"><div class="ci-icon"><i class="fa-solid fa-location-dot"></i></div><div class="ci-text"><span>Service Area</span>PLACEHOLDER_ADDRESS</div></div>
        <div class="ci-row"><div class="ci-icon"><i class="fa-solid fa-clock"></i></div><div class="ci-text"><span>Emergency Line</span>Available 24 Hours, 7 Days</div></div>
      </div>
    </div>
    <div class="contact-form anim right">
      <label><span class="cf-label">Full Name</span><input class="cf-input" type="text" placeholder="John Smith"></label>
      <div class="cf-row">
        <label><span class="cf-label">Email</span><input class="cf-input" type="email" placeholder="john@email.com"></label>
        <label><span class="cf-label">Phone</span><input class="cf-input" type="tel" placeholder="(330) 555-0000"></label>
      </div>
      <label><span class="cf-label">Service Needed</span>
        <select class="cf-select"><option value="">Select a service</option><option>Heating Repair</option><option>Furnace Replacement</option><option>AC Repair</option><option>AC Installation</option><option>Air Quality</option><option>Preventive Maintenance</option><option>Emergency Service</option><option>Commercial HVAC</option><option>Other</option></select>
      </label>
      <div class="cf-row">
        <label><span class="cf-label">Preferred Day</span><select class="cf-select"><option value="">Any Day</option><option>Monday</option><option>Tuesday</option><option>Wednesday</option><option>Thursday</option><option>Friday</option><option>Weekend</option></select></label>
        <label><span class="cf-label">Preferred Time</span><select class="cf-select"><option value="">Any Time</option><option>Morning (8am-12pm)</option><option>Afternoon (12pm-5pm)</option><option>Evening (5pm-8pm)</option></select></label>
      </div>
      <label><span class="cf-label">Message (optional)</span><textarea class="cf-textarea" placeholder="Describe your issue or question..."></textarea></label>
      <button class="cf-submit" type="button">Send My Request <i class="fa-solid fa-paper-plane"></i></button>
    </div>
  </div>
</section>

<footer>
  <div class="footer-inner">
    <div class="footer-grid">
      <div class="footer-brand">
        <span class="footer-logo-text">PLACEHOLDER_COMPANY</span>
        <p>Your trusted local heating and cooling experts. Licensed, insured, and committed to your comfort.</p>
        <div class="footer-social">
          <a href="#" class="social-btn"><i class="fa-brands fa-facebook-f"></i></a>
          <a href="#" class="social-btn"><i class="fa-brands fa-google"></i></a>
          <a href="#" class="social-btn"><i class="fa-brands fa-yelp"></i></a>
        </div>
      </div>
      <div class="footer-col"><h4>Services</h4><ul><li><a href="#services">Heating Repair &amp; Install</a></li><li><a href="#services">Air Conditioning</a></li><li><a href="#services">Air Quality</a></li><li><a href="#services">Emergency Service</a></li><li><a href="#services">Preventive Maintenance</a></li><li><a href="#services">Commercial HVAC</a></li></ul></div>
      <div class="footer-col"><h4>Service Area</h4><ul><li>PLACEHOLDER_ADDRESS</li><li>Surrounding Areas</li><li>Call to Confirm Coverage</li></ul></div>
      <div class="footer-col"><h4>Contact</h4><ul><li><a href="tel:PLACEHOLDER_PHONE_DIGITS">PLACEHOLDER_PHONE</a></li><li>PLACEHOLDER_EMAIL</li><li>Available 24/7 for emergencies</li><li>Free estimates on all jobs</li></ul></div>
    </div>
    <div class="footer-bottom">
      <p>&copy; 2025 PLACEHOLDER_COMPANY. All rights reserved.</p>
      <p>Licensed HVAC Contractor | Serving the Local Area</p>
    </div>
  </div>
</footer>

<script>
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
if (prefersReducedMotion) {
  document.querySelectorAll('.anim').forEach(el => el.classList.add('on'));
} else {
  const observer = new IntersectionObserver(entries => {
    entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('on'); observer.unobserve(e.target); } });
  }, { threshold: 0.12 });
  document.querySelectorAll('.anim').forEach(el => observer.observe(el));
}
</script>
</body>
</html>`;

// ── T5: Always On ─────────────────────────────────────────────────────────────
export const T5 = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="robots" content="noindex,nofollow">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <title>PLACEHOLDER_COMPANY | 24/7 Emergency HVAC</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="preconnect" href="https://cdnjs.cloudflare.com">
  <link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Barlow:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet">
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css">
  <style>
    :root {
      --coal:#1a1e28; --coal-deep:#0e1018;
      --orange:#f97316; --orange-deep:#c2560e; --orange-dim:rgba(249,115,22,0.12);
      --white:#ffffff; --off-white:#f8f9fa; --border:#e5e7eb;
      --text:#1a1e28; --text-mid:#374151; --muted:#6b7280;
      --text-dk:#edf0f7; --muted-dk:#7a8399; --container:1180px;
    }
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
    html{scroll-behavior:smooth}
    body{font-family:'Barlow',sans-serif;background:var(--white);color:var(--text);overflow-x:hidden}
    img{display:block;max-width:100%}
    h1,h2,h3{text-wrap:balance}
    #services,#about,#testimonials,#contact{scroll-margin-top:64px}
    a:focus-visible,button:focus-visible,input:focus-visible,select:focus-visible,textarea:focus-visible{outline:2px solid var(--orange);outline-offset:3px}
    .sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
    .skip-link{position:absolute;top:-100%;left:12px;z-index:9999;background:var(--orange);color:#fff;font-family:'Barlow Condensed',sans-serif;font-size:14px;font-weight:700;letter-spacing:.06em;padding:10px 20px;text-decoration:none;border-radius:4px;transition:top .2s}
    .skip-link:focus{top:12px}
    .container{max-width:var(--container);margin:0 auto;padding:0 48px}

    /* EMERG BAR */
    .emerg-bar{position:relative;overflow:hidden;background:var(--orange);height:40px;padding:0 48px;display:flex;align-items:center;justify-content:center;gap:20px}
    .emerg-bar::before{content:'';position:absolute;inset:0;pointer-events:none;background-image:repeating-linear-gradient(-45deg,rgba(0,0,0,.09) 0,rgba(0,0,0,.09) 6px,transparent 6px,transparent 16px)}
    .emerg-led{width:8px;height:8px;border-radius:50%;background:#fff;flex-shrink:0;box-shadow:0 0 8px #fff,0 0 16px rgba(255,255,255,.5);animation:led 1.1s ease-in-out infinite}
    @keyframes led{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.38;transform:scale(.7)}}
    .emerg-text{font-family:'IBM Plex Mono',monospace;font-size:11px;font-weight:500;color:#fff;letter-spacing:.12em;text-transform:uppercase;position:relative}
    .emerg-text a{color:#fff;font-weight:600;text-decoration:none;border-bottom:1px solid rgba(255,255,255,.55);transition:border-color .2s}
    .emerg-text a:hover{border-color:#fff}

    /* NAV */
    nav{position:sticky;top:0;z-index:200;height:64px;display:grid;grid-template-columns:1fr auto 1fr;align-items:center;padding:0 48px;background:rgba(14,16,24,.97);backdrop-filter:blur(20px);border-bottom:2px solid var(--orange);box-shadow:0 2px 20px rgba(0,0,0,.3)}
    .nav-logo{display:flex;align-items:center;gap:12px;text-decoration:none;justify-self:start}
    .nav-logo-mark{width:36px;height:36px;background:var(--orange);border-radius:6px;display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;flex-shrink:0;box-shadow:0 0 16px rgba(249,115,22,.4)}
    .nav-logo-text{font-family:'Barlow Condensed',sans-serif;font-size:21px;font-weight:800;color:var(--text-dk);letter-spacing:.01em;line-height:1.1}
    .nav-status{font-family:'IBM Plex Mono',monospace;font-size:9px;color:#22c55e;letter-spacing:.14em;text-transform:uppercase;display:flex;align-items:center;gap:5px}
    .nav-status-dot{width:6px;height:6px;border-radius:50%;background:#22c55e;box-shadow:0 0 6px rgba(34,197,94,.9);animation:led 2s ease-in-out infinite}
    .nav-links{display:flex;gap:36px;list-style:none;justify-self:center}
    .nav-links a{font-family:'Barlow Condensed',sans-serif;font-size:15px;font-weight:700;color:var(--muted-dk);text-decoration:none;letter-spacing:.06em;text-transform:uppercase;transition:color .2s}
    .nav-links a:hover{color:var(--orange)}
    .nav-right{display:flex;align-items:center;gap:16px;justify-self:end}
    .nav-phone{font-family:'IBM Plex Mono',monospace;font-size:13px;color:var(--orange);text-decoration:none;display:flex;align-items:center;gap:7px}
    .nav-cta{background:var(--orange);color:#fff;font-family:'Barlow Condensed',sans-serif;font-size:13px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;padding:9px 20px;border-radius:4px;text-decoration:none;white-space:nowrap;touch-action:manipulation;transition:background .15s,transform .12s}
    .nav-cta:hover{background:var(--orange-deep);transform:translateY(-1px)}

    /* HERO */
    @keyframes hUp{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:none}}
    .hero-chip{animation:hUp .65s cubic-bezier(.22,1,.36,1) both .05s}
    .hero-h1{animation:hUp .70s cubic-bezier(.22,1,.36,1) both .18s}
    .hero-tagline{animation:hUp .70s cubic-bezier(.22,1,.36,1) both .30s}
    .hero-trust{animation:hUp .70s cubic-bezier(.22,1,.36,1) both .42s}
    .hero-actions{animation:hUp .70s cubic-bezier(.22,1,.36,1) both .54s}
    .hero-form{animation:hUp .80s cubic-bezier(.22,1,.36,1) both .28s}
    @media(prefers-reduced-motion:reduce){.hero-chip,.hero-h1,.hero-tagline,.hero-trust,.hero-actions,.hero-form{animation:none}}
    .hero{position:relative;min-height:72vh;overflow:hidden;display:flex;align-items:center}
    .hero-bg{position:absolute;inset:0;background-image:url('PLACEHOLDER_IMG/pexels-jose-andres-pacheco-cortes-3641213-5463575.jpg');background-size:cover;background-position:center;filter:brightness(.72) saturate(.85)}
    .hero-grid-overlay{position:absolute;inset:0;pointer-events:none;z-index:1;background-image:linear-gradient(rgba(249,115,22,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(249,115,22,.04) 1px,transparent 1px);background-size:52px 52px}
    .hero-overlay{position:absolute;inset:0;background:linear-gradient(105deg,rgba(14,16,24,.88) 0%,rgba(14,16,24,.72) 45%,rgba(14,16,24,.22) 100%)}
    .hero-inner{position:relative;z-index:2;max-width:var(--container);margin:0 auto;width:100%;padding:72px 48px;display:grid;grid-template-columns:1fr 1fr;gap:56px;align-items:center}
    .hero-left{display:flex;flex-direction:column}
    .hero-chip{display:inline-flex;align-items:center;gap:8px;font-family:'IBM Plex Mono',monospace;font-size:10px;font-weight:500;color:var(--orange);letter-spacing:.14em;text-transform:uppercase;margin-bottom:20px}
    .chip-dot{width:6px;height:6px;border-radius:50%;background:var(--orange);box-shadow:0 0 8px rgba(249,115,22,.8);animation:led 1.4s ease-in-out infinite}
    .hero-h1{font-family:'Barlow Condensed',sans-serif;font-size:clamp(52px,6vw,88px);font-weight:900;line-height:.93;letter-spacing:-.01em;text-transform:uppercase;color:#fff;margin-bottom:20px;text-shadow:0 2px 8px rgba(0,0,0,.4)}
    .hero-h1 em{color:var(--orange);font-style:normal;display:block}
    .hero-tagline{font-size:16px;color:rgba(224,229,236,.78);line-height:1.75;margin-bottom:28px;max-width:420px}
    .hero-trust{list-style:none;display:flex;flex-direction:column;gap:9px;margin-bottom:32px}
    .hero-trust-item{display:flex;align-items:center;gap:9px;font-family:'Barlow Condensed',sans-serif;font-size:14px;font-weight:700;color:rgba(224,229,236,.88);text-transform:uppercase;letter-spacing:.04em}
    .hero-trust-item i{color:var(--orange);font-size:11px;width:14px;text-align:center}
    .hero-actions{display:flex;gap:12px;flex-wrap:wrap}
    .btn-hero{background:var(--orange);color:#fff;border:none;border-radius:4px;padding:14px 28px;cursor:pointer;touch-action:manipulation;font-family:'Barlow Condensed',sans-serif;font-size:16px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;display:inline-flex;align-items:center;gap:10px;text-decoration:none;transition:background .15s,transform .12s}
    .btn-hero:hover{background:var(--orange-deep);transform:translateY(-2px)}
    .btn-ghost{color:rgba(224,229,236,.85);border:1px solid rgba(224,229,236,.25);background:transparent;border-radius:4px;padding:13px 24px;font-family:'Barlow Condensed',sans-serif;font-size:15px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;text-decoration:none;display:inline-flex;align-items:center;gap:10px;touch-action:manipulation;transition:border-color .2s,color .2s}
    .btn-ghost:hover{border-color:var(--orange);color:var(--orange)}
    .hero-right{display:flex;align-items:center;justify-content:flex-end}
    .hero-form-card{background:#fff;border-radius:10px;padding:36px 32px;width:100%;max-width:440px;box-shadow:0 24px 64px rgba(0,0,0,.35);border-top:3px solid var(--orange)}
    .hfc-eyebrow{font-family:'IBM Plex Mono',monospace;font-size:9px;color:var(--orange);letter-spacing:.18em;text-transform:uppercase;margin-bottom:4px;display:flex;align-items:center;gap:6px}
    .hfc-eyebrow::before{content:'//';opacity:.45}
    .hfc-title{font-family:'Barlow Condensed',sans-serif;font-size:22px;font-weight:900;color:var(--text);text-transform:uppercase;letter-spacing:.02em;display:flex;align-items:center;gap:8px;margin-bottom:24px}
    .hfc-title i{color:var(--orange)}
    .hfc-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px}
    .hfc-group{margin-bottom:12px}
    .hfc-label{display:block;font-family:'IBM Plex Mono',monospace;font-size:9px;color:var(--muted);letter-spacing:.12em;text-transform:uppercase;margin-bottom:5px}
    .hfc-input,.hfc-textarea{width:100%;border:1px solid var(--border);border-radius:6px;padding:11px 13px;font-family:'Barlow',sans-serif;font-size:14px;color:var(--text);background:#fff;outline:none;transition:border-color .2s}
    .hfc-input::placeholder,.hfc-textarea::placeholder{color:#9ca3af}
    .hfc-input:focus,.hfc-textarea:focus{border-color:var(--orange)}
    .hfc-textarea{resize:vertical;min-height:90px}
    .hfc-submit{width:100%;background:var(--orange);color:#fff;border:none;border-radius:6px;padding:14px 20px;cursor:pointer;touch-action:manipulation;margin-top:4px;font-family:'Barlow Condensed',sans-serif;font-size:16px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;display:flex;align-items:center;justify-content:center;gap:10px;transition:background .15s,transform .12s}
    .hfc-submit:hover{background:var(--orange-deep);transform:translateY(-1px)}

    /* TRUST STRIP */
    .trust-strip{background:var(--white);border-top:1px solid var(--border);border-bottom:1px solid var(--border)}
    .trust-strip-inner{max-width:var(--container);margin:0 auto;padding:0 48px;display:flex;justify-content:center;align-items:stretch;overflow-x:auto;scrollbar-width:none}
    .trust-strip-inner::-webkit-scrollbar{display:none}
    .trust-badge{display:flex;align-items:center;gap:9px;padding:16px 22px;font-family:'IBM Plex Mono',monospace;font-size:10px;color:var(--muted);letter-spacing:.08em;text-transform:uppercase;border-right:1px solid var(--border);white-space:nowrap}
    .trust-badge:last-child{border-right:none}
    .trust-badge i{color:var(--orange);font-size:13px;flex-shrink:0}

    /* SERVICES */
    .services{background:var(--white);padding:96px 0}
    .sec-eyebrow{font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:var(--orange);margin-bottom:12px;display:flex;align-items:center;gap:8px}
    .sec-eyebrow::before{content:'//';opacity:.4}
    .sec-head{font-family:'Barlow Condensed',sans-serif;font-size:clamp(40px,5vw,68px);font-weight:900;color:var(--text);letter-spacing:-.01em;text-transform:uppercase;line-height:.95;margin-bottom:52px}
    .sec-head em{color:var(--orange);font-style:normal}
    .services-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
    .srv-card{display:flex;flex-direction:column;gap:12px;background:var(--white);border-radius:10px;padding:32px 26px 28px;position:relative;overflow:hidden;text-decoration:none;color:inherit;border:1px solid var(--border);box-shadow:0 2px 8px rgba(0,0,0,.05);transition:box-shadow .3s,transform .3s}
    .srv-card::after{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:var(--orange);transform:scaleX(0);transform-origin:left;transition:transform .35s ease}
    .srv-card:hover{transform:translateY(-4px);box-shadow:0 12px 32px rgba(0,0,0,.10)}
    .srv-card:hover::after{transform:scaleX(1)}
    .srv-icon{width:48px;height:48px;border-radius:10px;background:var(--orange-dim);display:flex;align-items:center;justify-content:center;color:var(--orange);font-size:20px;flex-shrink:0}
    .srv-card h3{font-family:'Barlow Condensed',sans-serif;font-size:19px;font-weight:800;color:var(--text);text-transform:uppercase;letter-spacing:.03em}
    .srv-card p{font-size:14px;color:var(--muted);line-height:1.72;flex:1}
    .srv-link{font-family:'IBM Plex Mono',monospace;font-size:11px;color:var(--orange);letter-spacing:.08em;text-transform:uppercase;display:flex;align-items:center;gap:6px;margin-top:4px;transition:gap .2s}
    .srv-card:hover .srv-link{gap:10px}

    /* PHOTO WALL */
    .photo-wall{display:grid;grid-template-columns:1fr 1.3fr .9fr;height:360px;gap:3px}
    .pw-cell{overflow:hidden;position:relative}
    .pw-cell img{width:100%;height:100%;object-fit:cover;display:block;filter:brightness(.75) saturate(.82)}
    .pw-overlay{position:absolute;inset:0;pointer-events:none;background:linear-gradient(to top,rgba(14,16,24,.6) 0%,transparent 55%)}

    /* STATS */
    .stats-strip{background:var(--coal);position:relative;overflow:hidden;border-top:3px solid var(--orange);border-bottom:1px solid rgba(249,115,22,.15)}
    .stats-strip::before{content:'';position:absolute;inset:0;pointer-events:none;background-image:linear-gradient(rgba(249,115,22,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(249,115,22,.04) 1px,transparent 1px);background-size:40px 40px}
    .stats-inner{position:relative;z-index:1;max-width:var(--container);margin:0 auto;display:grid;grid-template-columns:repeat(4,1fr);text-align:center}
    .stat-item{padding:52px 24px;border-right:1px solid rgba(249,115,22,.12);position:relative}
    .stat-item:last-child{border-right:none}
    .stat-item::before{content:'';position:absolute;top:0;left:50%;transform:translateX(-50%);width:28px;height:3px;background:var(--orange);box-shadow:0 0 8px rgba(249,115,22,.5)}
    .stat-num{font-family:'Barlow Condensed',sans-serif;font-size:60px;font-weight:900;color:var(--orange);line-height:1;letter-spacing:-.02em;display:block;text-transform:uppercase}
    .stat-label{font-family:'IBM Plex Mono',monospace;font-size:10px;color:var(--muted-dk);margin-top:8px;letter-spacing:.12em;text-transform:uppercase}

    /* ABOUT */
    .about{background:var(--off-white);padding:96px 0 148px;position:relative}
    .about::after{content:'';position:absolute;bottom:0;left:0;right:0;height:72px;background:var(--white);clip-path:polygon(0 100%,100% 0,100% 100%);pointer-events:none}
    .about-inner{max-width:var(--container);margin:0 auto;padding:0 48px;display:grid;grid-template-columns:1fr 1fr;gap:80px;align-items:center}
    .about-content p{font-size:15px;color:var(--muted);line-height:1.85;margin-bottom:28px}
    .about-feats{display:flex;flex-direction:column;gap:0;margin-bottom:36px}
    .about-feat{display:flex;align-items:flex-start;gap:14px;padding:18px 0;border-bottom:1px solid var(--border)}
    .about-feat:last-child{border-bottom:none}
    .feat-icon{width:40px;height:40px;background:var(--orange-dim);border-radius:8px;display:flex;align-items:center;justify-content:center;color:var(--orange);font-size:16px;flex-shrink:0}
    .feat-text h4{font-family:'Barlow Condensed',sans-serif;font-size:16px;font-weight:800;color:var(--text);text-transform:uppercase;letter-spacing:.03em;margin-bottom:3px}
    .feat-text p{font-size:13px;color:var(--muted);line-height:1.6}
    .btn-primary{background:var(--orange);color:#fff;border:none;border-radius:6px;padding:14px 32px;cursor:pointer;touch-action:manipulation;font-family:'Barlow Condensed',sans-serif;font-size:15px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;text-decoration:none;display:inline-flex;align-items:center;gap:10px;transition:background .15s,transform .12s}
    .btn-primary:hover{background:var(--orange-deep);transform:translateY(-2px)}
    .about-photos{display:grid;grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;gap:12px;height:440px}
    .about-photo-main{grid-row:1/3;overflow:hidden;border-radius:12px;box-shadow:0 8px 24px rgba(0,0,0,.12)}
    .about-photo-main img{width:100%;height:100%;object-fit:cover;display:block}
    .about-photo-sm{overflow:hidden;border-radius:10px;box-shadow:0 4px 14px rgba(0,0,0,.09)}
    .about-photo-sm img{width:100%;height:100%;object-fit:cover;display:block}

    /* TESTIMONIALS */
    .testimonials{background:var(--white);padding:96px 0 148px;position:relative}
    .testimonials::after{content:'';position:absolute;bottom:0;left:0;right:0;height:72px;background:var(--coal);clip-path:polygon(0 0,100% 100%,0 100%);pointer-events:none}
    .testi-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px}
    .testi-card{background:var(--off-white);border-radius:10px;padding:32px 28px;border-left:4px solid var(--orange);box-shadow:0 2px 8px rgba(0,0,0,.04)}
    .testi-stars{margin-bottom:14px;display:flex;gap:3px}
    .testi-stars i{color:#f59e0b;font-size:14px}
    .testi-quote{font-size:15px;color:var(--text-mid);line-height:1.75;margin-bottom:20px;font-style:italic}
    .testi-divider{width:28px;height:2px;background:var(--orange);margin-bottom:14px}
    .testi-name{font-family:'Barlow Condensed',sans-serif;font-size:15px;font-weight:800;color:var(--text);text-transform:uppercase;letter-spacing:.04em}
    .testi-location{font-family:'IBM Plex Mono',monospace;font-size:10px;color:var(--muted);text-transform:uppercase;letter-spacing:.1em;margin-top:3px}

    /* CTA BANNER */
    .cta-banner{background:var(--coal);padding:72px 0;position:relative;overflow:hidden}
    .cta-banner::before{content:'';position:absolute;inset:0;pointer-events:none;background-image:linear-gradient(rgba(249,115,22,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(249,115,22,.05) 1px,transparent 1px);background-size:52px 52px}
    .cta-banner-inner{max-width:var(--container);margin:0 auto;padding:0 48px;display:flex;align-items:center;justify-content:space-between;gap:48px;position:relative;z-index:1}
    .cta-h{font-family:'Barlow Condensed',sans-serif;font-size:clamp(36px,4vw,58px);font-weight:900;color:#fff;text-transform:uppercase;letter-spacing:-.01em;line-height:.95}
    .cta-h em{color:var(--orange);font-style:normal}
    .cta-sub{font-size:15px;color:rgba(224,229,236,.62);line-height:1.65;margin-top:12px;max-width:480px}
    .cta-right{display:flex;flex-direction:column;gap:12px;align-items:flex-start;flex-shrink:0}
    .btn-cta{background:var(--orange);color:#fff;font-family:'Barlow Condensed',sans-serif;font-size:16px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;padding:16px 36px;border-radius:4px;text-decoration:none;display:inline-flex;align-items:center;gap:10px;transition:background .15s,transform .12s;white-space:nowrap}
    .btn-cta:hover{background:var(--orange-deep);transform:translateY(-2px)}
    .cta-mono{font-family:'IBM Plex Mono',monospace;font-size:10px;color:var(--muted-dk);letter-spacing:.1em;text-transform:uppercase}

    /* CONTACT */
    .contact{padding:96px 0;background:var(--off-white)}
    .contact-inner{max-width:var(--container);margin:0 auto;padding:0 48px;display:grid;grid-template-columns:1fr 1.4fr;gap:80px;align-items:start}
    .contact-left h2{font-family:'Barlow Condensed',sans-serif;font-size:clamp(36px,4vw,56px);font-weight:900;color:var(--text);letter-spacing:-.01em;text-transform:uppercase;line-height:.95;margin-bottom:16px}
    .contact-left h2 em{color:var(--orange);font-style:normal}
    .contact-left>p{font-size:15px;color:var(--muted);line-height:1.75;margin-bottom:32px}
    .contact-info{display:flex;flex-direction:column;gap:12px}
    .ci-row{display:flex;align-items:center;gap:14px;padding:14px 18px;background:var(--white);border-left:4px solid var(--orange);border-radius:0 8px 8px 0;box-shadow:0 2px 6px rgba(0,0,0,.05);transition:transform .2s}
    .ci-row:hover{transform:translateX(4px)}
    .ci-icon{color:var(--orange);font-size:15px;flex-shrink:0}
    .ci-text{font-size:14px;font-weight:600;color:var(--text)}
    .ci-text span{display:block;font-family:'IBM Plex Mono',monospace;font-size:9px;color:var(--muted);text-transform:uppercase;letter-spacing:.12em;margin-bottom:2px}
    .contact-form{background:var(--white);border-radius:12px;padding:40px;border-top:3px solid var(--orange);box-shadow:0 4px 20px rgba(0,0,0,.07);display:flex;flex-direction:column;gap:16px}
    .cf-form-title{font-family:'Barlow Condensed',sans-serif;font-size:18px;font-weight:800;color:var(--text);text-transform:uppercase;letter-spacing:.04em;margin-bottom:4px}
    .cf-row{display:grid;grid-template-columns:1fr 1fr;gap:14px}
    .cf-label{font-family:'IBM Plex Mono',monospace;font-size:10px;color:var(--muted);text-transform:uppercase;letter-spacing:.12em;margin-bottom:6px;display:block}
    .cf-input,.cf-select,.cf-textarea{width:100%;border:1px solid var(--border);border-radius:6px;padding:12px 14px;font-family:'Barlow',sans-serif;font-size:14px;color:var(--text);background:var(--white);outline:none;transition:border-color .2s}
    .cf-input::placeholder,.cf-textarea::placeholder{color:#9ca3af}
    .cf-input:focus,.cf-select:focus,.cf-textarea:focus{border-color:var(--orange)}
    .cf-select{appearance:none;cursor:pointer;color:var(--muted)}
    .cf-select.has-value{color:var(--text)}
    .cf-textarea{resize:vertical;min-height:100px}
    .cf-submit{background:var(--orange);color:#fff;border:none;border-radius:8px;padding:15px 28px;cursor:pointer;width:100%;margin-top:4px;touch-action:manipulation;font-family:'Barlow Condensed',sans-serif;font-size:16px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;display:flex;align-items:center;justify-content:center;gap:10px;transition:background .15s,transform .12s}
    .cf-submit:hover{background:var(--orange-deep);transform:translateY(-1px)}

    /* FOOTER */
    footer{background:var(--coal-deep);border-top:1px solid rgba(249,115,22,.12);padding:64px 0 40px}
    .footer-inner{max-width:var(--container);margin:0 auto;padding:0 48px}
    .footer-grid{display:grid;grid-template-columns:1.4fr 1fr 1fr 1fr;gap:48px;margin-bottom:48px}
    .footer-brand-logo{display:flex;align-items:center;gap:10px;margin-bottom:14px}
    .footer-logo-mark{width:28px;height:28px;background:var(--orange);border-radius:4px;display:flex;align-items:center;justify-content:center;color:#fff;font-size:13px}
    .footer-logo-text{font-family:'Barlow Condensed',sans-serif;font-size:18px;font-weight:800;color:var(--text-dk)}
    .footer-brand>p{font-size:13px;color:#8b97af;line-height:1.75;margin-bottom:20px}
    .footer-social{display:flex;gap:8px}
    .social-btn{width:32px;height:32px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:4px;display:flex;align-items:center;justify-content:center;color:var(--muted-dk);font-size:12px;text-decoration:none;transition:background .2s,color .2s,border-color .2s}
    .social-btn:hover{background:var(--orange);color:#fff;border-color:var(--orange)}
    .footer-col h4{font-family:'IBM Plex Mono',monospace;font-size:10px;color:var(--muted-dk);text-transform:uppercase;letter-spacing:.14em;margin-bottom:16px}
    .footer-col ul{list-style:none;display:flex;flex-direction:column;gap:9px}
    .footer-col ul li{font-size:13px;color:#8b97af}
    .footer-col a{color:#8b97af;text-decoration:none;transition:color .2s}
    .footer-col a:hover{color:var(--orange)}
    .footer-bottom{border-top:1px solid rgba(249,115,22,.08);padding-top:24px;display:flex;justify-content:space-between;align-items:center}
    .footer-bottom p{font-family:'IBM Plex Mono',monospace;font-size:11px;color:#7a8399}

    /* ANIMATIONS */
    .anim{opacity:0;transform:translateY(28px);transition:opacity .65s ease,transform .65s ease}
    .anim.left{transform:translateX(-28px)}
    .anim.right{transform:translateX(28px)}
    .anim.on{opacity:1;transform:none}
    .d1{transition-delay:.07s}.d2{transition-delay:.14s}.d3{transition-delay:.21s}
    .d4{transition-delay:.28s}.d5{transition-delay:.35s}.d6{transition-delay:.42s}
    @media(prefers-reduced-motion:reduce){.anim{opacity:1;transform:none;transition:none}}

    /* RESPONSIVE */
    @media(max-width:1024px){
      .hero-inner{grid-template-columns:1fr;padding:56px 40px;gap:40px}
      .hero-left{align-items:center;text-align:center}
      .hero-tagline{margin-left:auto;margin-right:auto}
      .hero-trust{align-items:center}
      .hero-actions{justify-content:center}
      .hero-right{justify-content:center}
      .hero-form-card{max-width:100%}
      .services-grid{grid-template-columns:repeat(2,1fr)}
      .about-inner{grid-template-columns:1fr;gap:48px}
      .testi-grid{grid-template-columns:1fr 1fr}
      .cta-banner-inner{flex-direction:column;align-items:flex-start;gap:32px}
      .contact-inner{grid-template-columns:1fr;gap:48px}
      .footer-grid{grid-template-columns:1fr 1fr;gap:32px}
      .stats-inner{grid-template-columns:repeat(2,1fr)}
      .photo-wall{grid-template-columns:1fr 1fr;height:280px}
      .photo-wall .pw-cell:last-child{display:none}
    }
    @media(max-width:768px){
      .emerg-bar{padding:0 20px}
      nav{padding:0 20px;height:58px;grid-template-columns:auto 1fr auto}
      .nav-links{display:none}
      .nav-phone{display:none}
      .hero-inner{padding:40px 24px;gap:32px}
      .hfc-row{grid-template-columns:1fr}
      .trust-strip-inner{padding:0 24px;flex-wrap:wrap}
      .trust-badge{border-right:none;border-bottom:1px solid var(--border);flex:0 0 50%;padding:12px 16px}
      .container{padding:0 24px}
      .services{padding:60px 0}
      .services-grid{grid-template-columns:1fr}
      .photo-wall{height:200px;grid-template-columns:1fr 1fr}
      .photo-wall .pw-cell:last-child{display:none}
      .stats-inner{grid-template-columns:repeat(2,1fr)}
      .stat-item{padding:36px 16px;border-right:none;border-bottom:1px solid rgba(249,115,22,.1)}
      .stat-item:nth-child(odd){border-right:1px solid rgba(249,115,22,.1)}
      .stat-num{font-size:50px}
      .about{padding:60px 0 120px}
      .about-inner{padding:0 24px}
      .about-photos{height:260px}
      .about-photo-sm{display:none}
      .about-photo-main{grid-row:auto}
      .testimonials{padding:60px 0 120px}
      .testi-grid{grid-template-columns:1fr}
      .cta-banner{padding:56px 0}
      .cta-banner-inner{padding:0 24px}
      .contact{padding:60px 0}
      .contact-inner{padding:0 24px}
      .contact-form{padding:28px 22px}
      .cf-row{grid-template-columns:1fr}
      footer{padding:48px 0 32px}
      .footer-inner{padding:0 24px}
      .footer-grid{grid-template-columns:1fr;gap:28px}
      .footer-bottom{flex-direction:column;gap:8px;text-align:center}
    }
  </style>
</head>
<body>

<a href="#main" class="skip-link">Skip to main content</a>

<div class="emerg-bar" aria-label="Emergency service announcement">
  <div class="emerg-led" aria-hidden="true"></div>
  <span class="emerg-text">24/7 Emergency HVAC. Call Now: <a href="tel:PLACEHOLDER_PHONE_DIGITS" aria-label="Call PLACEHOLDER_PHONE">PLACEHOLDER_PHONE</a></span>
  <div class="emerg-led" aria-hidden="true"></div>
</div>

<nav aria-label="Main navigation">
  PLACEHOLDER_NAV_LOGO_T5
  <ul class="nav-links">
    <li><a href="#services">Services</a></li>
    <li><a href="#about">About</a></li>
    <li><a href="#testimonials">Reviews</a></li>
    <li><a href="#contact">Contact</a></li>
  </ul>
  <div class="nav-right">
    <a href="tel:PLACEHOLDER_PHONE_DIGITS" class="nav-phone" aria-label="Call PLACEHOLDER_PHONE"><i class="fa-solid fa-phone" aria-hidden="true"></i>PLACEHOLDER_PHONE</a>
    <a href="#contact" class="nav-cta">Free Estimate</a>
  </div>
</nav>

<section class="hero" id="main">
  <div class="hero-bg" aria-hidden="true"></div>
  <div class="hero-grid-overlay" aria-hidden="true"></div>
  <div class="hero-overlay" aria-hidden="true"></div>
  <div class="hero-inner">
    <div class="hero-left">
      <h1 class="hero-h1">PLACEHOLDER_HERO_H1</h1>
      <p class="hero-tagline">PLACEHOLDER_HERO_TAGLINE</p>
      <ul class="hero-trust" aria-label="Why choose us">
        <li class="hero-trust-item"><i class="fa-solid fa-check" aria-hidden="true"></i>Licensed, bonded &amp; fully insured</li>
        <li class="hero-trust-item"><i class="fa-solid fa-check" aria-hidden="true"></i>Same-day &amp; 24/7 emergency service</li>
        <li class="hero-trust-item"><i class="fa-solid fa-check" aria-hidden="true"></i>No after-hours upcharge, ever</li>
        <li class="hero-trust-item"><i class="fa-solid fa-check" aria-hidden="true"></i>45-min average response time</li>
      </ul>
      <div class="hero-actions">
        <a href="tel:PLACEHOLDER_PHONE_DIGITS" class="btn-hero"><i class="fa-solid fa-phone" aria-hidden="true"></i> Call Now — 24/7</a>
        <a href="#services" class="btn-ghost"><i class="fa-solid fa-list" aria-hidden="true"></i> See All Services</a>
      </div>
    </div>
    <div class="hero-right">
      <div class="hero-form-card hero-form" role="form" aria-label="Request a free estimate">
        <div class="hfc-eyebrow">Quick Dispatch</div>
        <div class="hfc-title"><i class="fa-solid fa-bolt" aria-hidden="true"></i> Get a Tech on the Way</div>
        <div class="hfc-row">
          <div class="hfc-group" style="margin-bottom:0"><label class="hfc-label" for="t5-first">First Name</label><input class="hfc-input" type="text" id="t5-first" name="first-name" autocomplete="given-name" placeholder="First Name"></div>
          <div class="hfc-group" style="margin-bottom:0"><label class="hfc-label" for="t5-last">Last Name</label><input class="hfc-input" type="text" id="t5-last" name="last-name" autocomplete="family-name" placeholder="Last Name"></div>
        </div>
        <div class="hfc-row">
          <div class="hfc-group" style="margin-bottom:0"><label class="hfc-label" for="t5-email">Email *</label><input class="hfc-input" type="email" id="t5-email" name="email" autocomplete="email" placeholder="Email"></div>
          <div class="hfc-group" style="margin-bottom:0"><label class="hfc-label" for="t5-phone">Phone *</label><input class="hfc-input" type="tel" id="t5-phone" name="tel" autocomplete="tel" placeholder="Phone"></div>
        </div>
        <div class="hfc-group">
          <label class="hfc-label" for="t5-message">Message Here</label>
          <textarea class="hfc-textarea" id="t5-message" name="message" placeholder="Describe your issue..."></textarea>
        </div>
        <button class="hfc-submit" type="button"><i class="fa-solid fa-bolt" aria-hidden="true"></i> Dispatch Now</button>
      </div>
    </div>
  </div>
</section>

<div class="trust-strip" aria-label="Service guarantees">
  <div class="trust-strip-inner">
    <div class="trust-badge"><i class="fa-solid fa-shield-halved" aria-hidden="true"></i>Licensed &amp; Insured</div>
    <div class="trust-badge"><i class="fa-solid fa-bolt" aria-hidden="true"></i>Same-Day Service</div>
    <div class="trust-badge"><i class="fa-solid fa-wrench" aria-hidden="true"></i>All Makes &amp; Models</div>
    <div class="trust-badge"><i class="fa-solid fa-dollar-sign" aria-hidden="true"></i>No After-Hours Upcharge</div>
    <div class="trust-badge"><i class="fa-solid fa-tag" aria-hidden="true"></i>Flat-Rate Pricing</div>
    <div class="trust-badge"><i class="fa-solid fa-star" aria-hidden="true"></i>Free Estimates</div>
  </div>
</div>

<section class="services" id="services">
  <div class="container">
    <div class="sec-eyebrow anim">Our Services</div>
    <div class="sec-head anim d1">Heating &amp; Cooling<br><em>Done Right</em></div>
    <div class="services-grid">PLACEHOLDER_SERVICE_CARDS_T5</div>
  </div>
</section>

<div class="photo-wall" aria-hidden="true">
  <div class="pw-cell"><img src="PLACEHOLDER_IMG/compagnons-yZmHFF-g-W0-unsplash.jpg" alt="" loading="lazy"><div class="pw-overlay"></div></div>
  <div class="pw-cell"><img src="PLACEHOLDER_IMG/pexels-optical-chemist-340351297-36360393.jpg" alt="" loading="lazy"><div class="pw-overlay"></div></div>
  <div class="pw-cell"><img src="PLACEHOLDER_IMG/pexels-artbovich-7534232.jpg" alt="" loading="lazy"><div class="pw-overlay"></div></div>
</div>

<div class="stats-strip" aria-label="Company statistics">
  <div class="stats-inner">
    <div class="stat-item anim"><span class="stat-num">24/7</span><div class="stat-label">Emergency Service</div></div>
    <div class="stat-item anim d1"><span class="stat-num">45<small style="font-size:.52em;letter-spacing:0">min</small></span><div class="stat-label">Avg Response Time</div></div>
    <div class="stat-item anim d2"><span class="stat-num" data-count="18" data-suffix="+">18+</span><div class="stat-label">Years in Business</div></div>
    <div class="stat-item anim d3"><span class="stat-num">$0</span><div class="stat-label">Extra for After-Hours</div></div>
  </div>
</div>

<section class="about" id="about">
  <div class="about-inner">
    <div class="about-photos anim left">
      <div class="about-photo-main"><img src="PLACEHOLDER_IMG/pexels-pixabay-257770.jpg" alt="HVAC ductwork" loading="lazy"></div>
      <div class="about-photo-sm"><img src="PLACEHOLDER_IMG/pexels-soc-nang-d-ng-2150345854-36248286.jpg" alt="Ceiling vent" loading="lazy"></div>
      <div class="about-photo-sm"><img src="PLACEHOLDER_IMG/pexels-jose-andres-pacheco-cortes-3641213-5463575.jpg" alt="HVAC technician" loading="lazy"></div>
    </div>
    <div class="about-content anim right">
      <div class="sec-eyebrow">About PLACEHOLDER_COMPANY</div>
      <h2 class="sec-head" style="font-size:clamp(36px,4vw,54px);margin-bottom:20px;">Trusted by Homeowners<br>Across <em>PLACEHOLDER_EYEBROW</em></h2>
      <p>PLACEHOLDER_ABOUT_PARA</p>
      <div class="about-feats">
        <div class="about-feat">
          <div class="feat-icon" aria-hidden="true"><i class="fa-solid fa-bolt"></i></div>
          <div class="feat-text"><h4>45-Minute Average Response</h4><p>We dispatch immediately and reach most locations in under an hour.</p></div>
        </div>
        <div class="about-feat">
          <div class="feat-icon" aria-hidden="true"><i class="fa-solid fa-dollar-sign"></i></div>
          <div class="feat-text"><h4>No After-Hours Upcharges</h4><p>2am on a Sunday? Same rate as any other call. No surprises on your bill.</p></div>
        </div>
        <div class="about-feat">
          <div class="feat-icon" aria-hidden="true"><i class="fa-solid fa-shield-halved"></i></div>
          <div class="feat-text"><h4>Licensed, Bonded &amp; Insured</h4><p>Full contractor licensing, liability, and workers' comp on every job.</p></div>
        </div>
      </div>
      <a href="#contact" class="btn-primary"><i class="fa-solid fa-calendar-check" aria-hidden="true"></i> Request a Technician</a>
    </div>
  </div>
</section>

<section class="testimonials" id="testimonials">
  <div class="container">
    <div class="sec-eyebrow anim">Customer Reviews</div>
    <div class="sec-head anim d1">What Our Customers<br><em>Are Saying</em></div>
    <div class="testi-grid">
      <div class="testi-card anim d1">
        <div class="testi-stars" aria-label="5 out of 5 stars"><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i></div>
        <p class="testi-quote">"Our furnace died at midnight in January. The team had a technician here within 40 minutes and had us back up and running before 2am. Absolutely outstanding."</p>
        <div class="testi-divider"></div>
        <div class="testi-name">Mike R.</div><div class="testi-location">Strongsville, OH</div>
      </div>
      <div class="testi-card anim d2">
        <div class="testi-stars" aria-label="5 out of 5 stars"><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i></div>
        <p class="testi-quote">"Called on a Sunday afternoon when our AC went out. They quoted a flat rate before starting, showed up on time, fixed it same day. No extra charge for the weekend."</p>
        <div class="testi-divider"></div>
        <div class="testi-name">Sarah K.</div><div class="testi-location">Westlake, OH</div>
      </div>
      <div class="testi-card anim d3">
        <div class="testi-stars" aria-label="5 out of 5 stars"><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i><i class="fa-solid fa-star" aria-hidden="true"></i></div>
        <p class="testi-quote">"Used them for three years now. They installed our heat pump and handle our annual maintenance. Always professional, always on time. Highly recommended."</p>
        <div class="testi-divider"></div>
        <div class="testi-name">Dave &amp; Linda T.</div><div class="testi-location">Medina, OH</div>
      </div>
    </div>
  </div>
</section>

<div class="cta-banner">
  <div class="cta-banner-inner">
    <div>
      <h2 class="cta-h">System Down?<br><em>We're Always On.</em></h2>
      <p class="cta-sub">Emergency repairs dispatched immediately, 24 hours a day. No extra charge for nights or weekends.</p>
    </div>
    <div class="cta-right">
      <a href="tel:PLACEHOLDER_PHONE_DIGITS" class="btn-cta"><i class="fa-solid fa-phone" aria-hidden="true"></i> Call Now — PLACEHOLDER_PHONE</a>
      <span class="cta-mono">Or submit the form below for non-emergencies</span>
    </div>
  </div>
</div>

<section class="contact" id="contact">
  <div class="contact-inner">
    <div class="contact-left anim left">
      <div class="sec-eyebrow">Get in Touch</div>
      <h2>Freeze or Heat Wave,<br>We <em>Answer.</em></h2>
      <p>Call or submit the form and we'll have a technician to you fast. Emergency calls are dispatched immediately, 24 hours a day.</p>
      <div class="contact-info">
        <div class="ci-row"><div class="ci-icon" aria-hidden="true"><i class="fa-solid fa-phone"></i></div><div class="ci-text"><span>Emergency Line</span><a href="tel:PLACEHOLDER_PHONE_DIGITS" style="color:inherit;text-decoration:none;">PLACEHOLDER_PHONE</a></div></div>
        <div class="ci-row"><div class="ci-icon" aria-hidden="true"><i class="fa-solid fa-envelope"></i></div><div class="ci-text"><span>Email</span><a href="mailto:PLACEHOLDER_EMAIL" style="color:inherit;text-decoration:none;">PLACEHOLDER_EMAIL</a></div></div>
        <div class="ci-row"><div class="ci-icon" aria-hidden="true"><i class="fa-solid fa-location-dot"></i></div><div class="ci-text"><span>Service Area</span>PLACEHOLDER_ADDRESS</div></div>
        <div class="ci-row"><div class="ci-icon" aria-hidden="true"><i class="fa-solid fa-clock"></i></div><div class="ci-text"><span>Hours</span>24 Hours, 7 Days a Week</div></div>
      </div>
    </div>
    <div class="contact-form anim right" role="form" aria-label="Request a service appointment">
      <p class="cf-form-title">Request Service</p>
      <label><span class="cf-label">Full Name</span><input class="cf-input" type="text" name="name" autocomplete="name" placeholder="Your name"></label>
      <div class="cf-row">
        <label><span class="cf-label">Email</span><input class="cf-input" type="email" name="email" autocomplete="email" placeholder="email@example.com"></label>
        <label><span class="cf-label">Phone</span><input class="cf-input" type="tel" name="tel" autocomplete="tel" placeholder="(330) 555-0000"></label>
      </div>
      <label><span class="cf-label">Service Needed</span>
        <select class="cf-select" name="service"><option value="">Select a service</option><option>Emergency Heating Repair</option><option>Emergency AC Repair</option><option>Furnace Replacement</option><option>AC Installation</option><option>Preventive Maintenance</option><option>Commercial HVAC</option><option>Other</option></select>
      </label>
      <div class="cf-row">
        <label><span class="cf-label">System Type</span>
          <select class="cf-select" name="system-type"><option value="">Not Sure</option><option>Gas Furnace</option><option>Heat Pump</option><option>Central AC</option><option>Mini-Split</option><option>Boiler</option><option>Other</option></select>
        </label>
        <label><span class="cf-label">Urgency</span>
          <select class="cf-select" name="urgency"><option value="">Routine</option><option>Emergency — ASAP</option><option>Today</option><option>This Week</option></select>
        </label>
      </div>
      <label><span class="cf-label">Describe the Problem</span><textarea class="cf-textarea" name="message" placeholder="What's happening with your system?"></textarea></label>
      <button class="cf-submit" type="button"><i class="fa-solid fa-bolt" aria-hidden="true"></i> Send My Request</button>
    </div>
  </div>
</section>

<footer>
  <div class="footer-inner">
    <div class="footer-grid">
      <div class="footer-brand">
        <div class="footer-brand-logo">
          <div class="footer-logo-mark" aria-hidden="true"><i class="fa-solid fa-fire-flame-curved"></i></div>
          <span class="footer-logo-text">PLACEHOLDER_COMPANY</span>
        </div>
        <p>Northeast Ohio's fastest emergency HVAC response. Licensed technicians available 24 hours, 7 days a week.</p>
        <div class="footer-social">
          <a href="#" class="social-btn" aria-label="Facebook"><i class="fa-brands fa-facebook-f" aria-hidden="true"></i></a>
          <a href="#" class="social-btn" aria-label="Google"><i class="fa-brands fa-google" aria-hidden="true"></i></a>
          <a href="#" class="social-btn" aria-label="Yelp"><i class="fa-brands fa-yelp" aria-hidden="true"></i></a>
        </div>
      </div>
      <div class="footer-col"><h4>Services</h4><ul><li><a href="#services">Emergency Heating</a></li><li><a href="#services">Emergency Cooling</a></li><li><a href="#services">24/7 Dispatch</a></li><li><a href="#services">All Makes &amp; Models</a></li><li><a href="#services">Flat-Rate Pricing</a></li><li><a href="#services">Maintenance Plans</a></li></ul></div>
      <div class="footer-col"><h4>Service Area</h4><ul><li>PLACEHOLDER_ADDRESS</li><li>Surrounding Areas</li><li>Call to Confirm Coverage</li></ul></div>
      <div class="footer-col"><h4>Contact</h4><ul><li><a href="tel:PLACEHOLDER_PHONE_DIGITS">PLACEHOLDER_PHONE</a></li><li>PLACEHOLDER_EMAIL</li><li>Open 24/7, No Extra Charge</li><li>Free Estimates on All Jobs</li></ul></div>
    </div>
    <div class="footer-bottom">
      <p>&copy; 2025 PLACEHOLDER_COMPANY. All rights reserved.</p>
      <p>Licensed HVAC Contractor | Available 24/7</p>
    </div>
  </div>
</footer>

<script>
document.querySelectorAll('.cf-select').forEach(sel => {
  const update = () => sel.classList.toggle('has-value', sel.value !== '');
  sel.addEventListener('change', update);
  update();
});
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
if (prefersReducedMotion) {
  document.querySelectorAll('.anim').forEach(el => el.classList.add('on'));
} else {
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('on'); obs.unobserve(e.target); } });
  }, { threshold: 0.12 });
  document.querySelectorAll('.anim').forEach(el => obs.observe(el));
  function animateCounter(el) {
    const target = parseInt(el.dataset.count, 10);
    if (!target) return;
    const suffix = el.dataset.suffix || '';
    const duration = 1400, start = performance.now();
    (function step(now) {
      const p = Math.min((now - start) / duration, 1);
      el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3))) + suffix;
      if (p < 1) requestAnimationFrame(step);
    })(start);
  }
  const cObs = new IntersectionObserver(entries => {
    entries.forEach(e => { if (e.isIntersecting) { animateCounter(e.target); cObs.unobserve(e.target); } });
  }, { threshold: 0.5 });
  document.querySelectorAll('.stat-num[data-count]').forEach(el => cObs.observe(el));
}
</script>
</body>
</html>`;

export function generateHVACMockup(templateId, data) {
  const templates = { 4: T4, 5: T5 };
  let html = templates[templateId];
  if (!html) return '';

  const company = (data.company || 'Your Company').replace(/&/g, '&amp;');
  const phone = data.phone || '330-000-0000';
  const phoneDigits = phone.replace(/\D/g, '');
  const email = data.email || 'contact@yourcompany.com';
  const address = data.address || 'Northeast Ohio';
  const logoUrl = data.logoUrl;
  const copy = data.copy;
  const defaults = HVAC_DEFAULT_COPY[templateId];

  const navLogoT4 = logoUrl
    ? `<a href="#" class="nav-logo"><img src="${logoUrl}" alt="${company}" style="height:36px;width:auto;"><span class="sr-only">${company}</span></a>`
    : `<a href="#" class="nav-logo"><div class="nav-logo-icon"><i class="fa-solid fa-temperature-half"></i></div><span class="nav-logo-text">${company}</span></a>`;

  const navLogoT5 = logoUrl
    ? `<a href="#" class="nav-logo"><img src="${logoUrl}" alt="${company}" style="height:36px;width:auto;filter:brightness(0) invert(1);"><span class="sr-only">${company}</span></a>`
    : `<a href="#" class="nav-logo"><div class="nav-logo-mark"><i class="fa-solid fa-fire-flame-curved"></i></div><div><span class="nav-logo-text">${company}</span><div class="nav-status"><span class="nav-status-dot"></span>Dispatch Active</div></div></a>`;

  html = html.replace('PLACEHOLDER_NAV_LOGO_T4', navLogoT4);
  html = html.replace('PLACEHOLDER_NAV_LOGO_T5', navLogoT5);

  const defaultSvcs = HVAC_DEFAULT_SERVICES[templateId];
  const rawServices = (copy && Array.isArray(copy.services) && copy.services.length >= 3)
    ? copy.services.slice(0, 6) : defaultSvcs;
  // Normalize: AI copy returns {name, desc}; defaults have {title, icon, desc}
  const services = rawServices.map((s, i) => ({
    title: s.title || s.name || 'HVAC Service',
    icon: s.icon || defaultSvcs[i % defaultSvcs.length].icon,
    desc: s.desc || '',
  }));

  html = html.replace(`PLACEHOLDER_SERVICE_CARDS_T${templateId}`,
    templateId === 4 ? buildHVACServiceCardsT4(services) : buildHVACServiceCardsT5(services));

  const eyebrow = (copy && copy.eyebrow) || defaults.eyebrow;
  const headline = (copy && copy.headline) ? copy.headline.replace(/\n/g, '<br>') : defaults.headline;
  const tagline = (copy && copy.tagline) || defaults.tagline;
  const about = (copy && copy.about) || defaults.about.replace('COMPANY_NAME', company);

  html = html.replace('PLACEHOLDER_HERO_EYEBROW', eyebrow);
  html = html.replace('PLACEHOLDER_HERO_H1', headline);
  html = html.replace('PLACEHOLDER_HERO_TAGLINE', tagline);
  html = html.replace('PLACEHOLDER_ABOUT_PARA', about);
  html = html.replace(/PLACEHOLDER_COMPANY/g, company);
  html = html.replace(/PLACEHOLDER_PHONE_DIGITS/g, phoneDigits);
  html = html.replace(/PLACEHOLDER_PHONE/g, phone);
  html = html.replace(/PLACEHOLDER_EMAIL/g, email);
  html = html.replace(/PLACEHOLDER_ADDRESS/g, address);
  html = html.replace(/PLACEHOLDER_IMG/g, 'https://semdeckdigital.github.io/semdeck-claude-mockups/images');

  return html;
}
